// Eskom PARCC — dataset inference (ported from the original `inferDataset`, spec §4.3).
//
// Explicit dataset text wins; otherwise the Eskom reference-number prefix decides.
//
// R6 (Lindiwe, 23 Jul 2026) reshaped this in three ways:
//
//   1. POs and LPOs are SEPARATE buckets, not one 'POs / LPOs'. The old
//      `\b(po|pos|lpo|lpos)\b` alternation could not tell them apart at all.
//   2. A Panel release is its own bucket, ahead of everything else. Her sheet
//      types those 14 rows 'Contracts' and flags them only in the
//      "Panel Release(Y/N)" column, so the flag — not the text — has to decide.
//   3. 'Task Orders' is recognised explicitly. The old cascade had no branch for
//      it, so all 49 of her task orders fell through to the prefix rule and
//      scattered into Contracts (11), POs/LPOs (26) and Other (12).
//
// Reference prefixes, measured across her 294-row population: 43… = LPOs,
// 45… = POs, 46… = Contracts. Task orders span 45/46/60 and are identifiable
// ONLY from the explicit text — which is why the text branch runs first.

import type { Dataset } from './enums';

export function inferDataset(
  explicit?: string | null,
  referenceNo?: string | null,
  /** The "Panel Release(Y/N)" flag. True wins over every other signal. */
  panelRelease?: boolean | null,
): Dataset {
  // 1. A panel release is a panel release, whatever the Dataset cell says — the
  //    whole point of the R6 ask is that these stop being counted as Contracts.
  if (panelRelease === true) return 'Panels';

  const e = (explicit ?? '').trim().toLowerCase();
  if (e) {
    // Order is load-bearing:
    //   • task order BEFORE the PO tests — "Task Order" must not be read as a PO;
    //   • lpo BEFORE po — 'LPOs' contains the substring 'po';
    //   • panel before contract — a "Panel Contract" is a panel.
    if (/task\s*order/.test(e)) return 'Task Orders';
    if (/\blpos?\b|local purchase/.test(e)) return 'LPOs';
    if (/\bpos?\b|purchase order/.test(e)) return 'POs';
    if (/panel/.test(e)) return 'Panels';
    if (/contract/.test(e)) return 'Contracts';
  }

  // 2. Fall back to the Eskom reference-number prefix.
  const ref = (referenceNo ?? '').trim();
  if (/^46\d{8,}/.test(ref)) return 'Contracts';
  if (/^45\d{8,}/.test(ref)) return 'POs';
  if (/^43\d{8,}/.test(ref)) return 'LPOs';
  return 'Other';
}
